import { useState } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@wondermaps/ui";
import { VisitorDrawer } from "./VisitorDrawer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { faChevronUp } from "@fortawesome/pro-solid-svg-icons/faChevronUp";
import { faLocationArrow } from "@fortawesome/pro-solid-svg-icons/faLocationArrow";
import { faArrowUp } from "@fortawesome/pro-solid-svg-icons/faArrowUp";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons/faArrowRight";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons/faArrowLeft";
import { faArrowTurnRight } from "@fortawesome/pro-solid-svg-icons/faArrowTurnRight";
import { faArrowTurnLeft } from "@fortawesome/pro-solid-svg-icons/faArrowTurnLeft";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons/faTriangleExclamation";
import { faWheelchair } from "@fortawesome/pro-solid-svg-icons/faWheelchair";
import { faPersonWalking } from "@fortawesome/pro-solid-svg-icons/faPersonWalking";
import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import { faTag } from "@fortawesome/pro-solid-svg-icons/faTag";
import { POISheetHeader } from "./poi/POISheetHeader";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { Route, RouteInstruction } from "@wondermaps/maps";
import { formatDistance } from "../../lib/format-distance";
import { formatWalkTime } from "../../lib/walk-time";
import { contrastText } from "../../lib/color-utils";
import { findIconByName } from "../../data/fontawesome-icons";

interface DirectionsPanelProps {
  route: Route | null;
  destinationId?: string;
  destinationName?: string | null;
  categoryName?: string | null;
  categoryIcon?: string | null;
  /** X button — fully cancels navigation */
  onClose: () => void;
  /** Cancel button — returns to route preview */
  onCancel: () => void;
  accessibleMode?: boolean;
  overlayAccessible?: boolean;
  manualAccessibleMode?: boolean;
  isFallbackRoute?: boolean;
  onToggleAccessible?: () => void;
  currentInstructionIndex?: number;
  isOffPath?: boolean;
  onReroute?: () => void;
  remainingDistance?: number;
  remainingTime?: number;
  brandAccent?: string | null;
  isBookmarked?: boolean;
  onBookmarkToggle?: (id: string) => void;
  bookmarksEnabled?: boolean;
}

function formatTime(seconds: number, t?: TFunction): string {
  const minutes = Math.round(seconds / 60);
  if (minutes < 1) return t ? t("common:time.lessThan1Min") : "< 1 min";
  return t ? t("common:time.minutes", { count: minutes }) : `${minutes} min`;
}

const DIRECTION_ICON_MAP: Record<string, { icon: IconDefinition; rotate?: string }> = {
  start: { icon: faLocationArrow },
  continueStraight: { icon: faArrowUp },
  slightRight: { icon: faArrowTurnRight, rotate: "-rotate-12" },
  right: { icon: faArrowRight },
  sharpRight: { icon: faArrowTurnRight, rotate: "rotate-45" },
  slightLeft: { icon: faArrowTurnLeft, rotate: "rotate-12" },
  left: { icon: faArrowLeft },
  sharpLeft: { icon: faArrowTurnLeft, rotate: "-rotate-45" },
};

function getInstructionText(
  instruction: RouteInstruction,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  const direction = t(`directions.${instruction.directionType}`);
  if (instruction.type === "start" || instruction.type === "arrive") return direction;
  if (instruction.landmark) {
    return t("directions.atLandmark", { direction, landmark: instruction.landmark });
  }
  return t("directions.inDistance", {
    distance: Math.round(instruction.distance),
    direction: direction.toLowerCase(),
  });
}

export function DirectionsPanel({
  route,
  destinationId,
  destinationName,
  categoryName,
  categoryIcon,
  onClose,
  onCancel,
  accessibleMode,
  overlayAccessible,
  manualAccessibleMode,
  isFallbackRoute,
  onToggleAccessible,
  currentInstructionIndex = 0,
  isOffPath,
  onReroute,
  remainingDistance,
  remainingTime,
  brandAccent,
  isBookmarked,
  onBookmarkToggle,
  bookmarksEnabled,
}: DirectionsPanelProps) {
  const { t, i18n } = useTranslation("map");
  const [isExpanded, setIsExpanded] = useState(false);

  const bg = brandAccent ?? "#0d9488";
  const fg = contrastText(bg);

  const currentInstruction = route?.instructions[currentInstructionIndex];
  const remainingInstructions = route?.instructions.slice(currentInstructionIndex + 1) ?? [];
  const currentEntry = currentInstruction ? DIRECTION_ICON_MAP[currentInstruction.directionType] : null;

  const timeText = formatTime(remainingTime ?? route?.estimatedTime ?? 0, t);
  const distText = formatDistance(remainingDistance ?? route?.totalDistance ?? 0, i18n.language, t);
  const walkTime = formatWalkTime(route?.totalDistance ?? 0, accessibleMode, t);

  const catIcon = categoryIcon ? findIconByName(categoryIcon) : null;

  return (
    <VisitorDrawer
      open
      modal={false}
      maxHeightClass="max-h-[60vh]"
      className="overflow-hidden"
      dismissible={false}
      handleColor="rgba(255,255,255,0.16)"
      contentStyle={{ backgroundColor: bg }}
    >
      <div className="flex flex-col h-full" style={{ color: fg }}>

        {/* Header: destination name + heart + X */}
        <POISheetHeader
          title={destinationName ?? ""}
          subtitle={[
            ...(categoryName ? [{ icon: catIcon?.icon ?? faTag, text: categoryName }] : []),
            { icon: accessibleMode ? faWheelchair : faPersonWalking, text: walkTime ? `${walkTime} · ${distText}` : `${timeText} · ${distText}` },
          ]}
          isBookmarked={isBookmarked}
          onBookmarkToggle={bookmarksEnabled && onBookmarkToggle && destinationId ? () => onBookmarkToggle(destinationId) : undefined}
          bookmarksEnabled={bookmarksEnabled}
          onClose={onClose}
          fg={fg}
          closePillBg="rgba(255,255,255,0.16)"
          subtitleOpacity={0.7}
          className="px-4 pt-2 pb-1"
        />

        {/* Large current instruction block */}
        <div className="flex items-center gap-4 px-4 py-3">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: "rgba(255,255,255,0.10)" }}
          >
            {currentInstruction?.type === "arrive" ? (
              <div className="h-4 w-4 rounded-full bg-white" />
            ) : currentEntry ? (
              <FontAwesomeIcon
                icon={currentEntry.icon}
                className={`h-7 w-7 ${currentEntry.rotate ?? ""}`}
                style={{ color: fg }}
              />
            ) : (
              <FontAwesomeIcon icon={faLocationArrow} className="h-7 w-7" style={{ color: fg }} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            {currentInstruction &&
              currentInstruction.type !== "start" &&
              currentInstruction.type !== "arrive" &&
              currentInstruction.distance > 0 && (
                <p className="text-[28px] font-bold leading-none">
                  {formatDistance(currentInstruction.distance, i18n.language, t)}
                </p>
              )}
            <p className="text-base mt-0.5 break-words">
              {currentInstruction
                ? getInstructionText(currentInstruction, t)
                : t("directions.navigating")}
            </p>
          </div>
        </div>

        {/* Off-path warning */}
        {isOffPath && onReroute && (
          <div
            className="mx-4 mb-2 flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-sm"
            style={{ backgroundColor: "rgba(255,255,255,0.10)" }}
          >
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4" />
              <span>{t("directions.offRoute")}</span>
            </div>
            <button
              type="button"
              onClick={onReroute}
              className="shrink-0 text-sm font-medium underline"
              style={{ color: fg }}
            >
              {t("directions.reroute")}
            </button>
          </div>
        )}

        {/* Disclaimers */}
        {isFallbackRoute && (
          <div className="mx-4 mb-2 rounded-xl px-3 py-2 text-sm"
            style={{ backgroundColor: "rgba(255,255,255,0.10)" }}>
            {t("directions.noAccessibleRoute")}
          </div>
        )}
        {!accessibleMode && route?.hasStairs && (
          <div className="mx-4 mb-2 rounded-xl px-3 py-2 text-sm"
            style={{ backgroundColor: "rgba(255,255,255,0.10)" }}>
            {t("directions.routeIncludesStairs")}
          </div>
        )}
        {!accessibleMode && route?.hasSteepGrade && (
          <div className="mx-4 mb-2 rounded-xl px-3 py-2 text-sm"
            style={{ backgroundColor: "rgba(255,255,255,0.10)" }}>
            {t("directions.routeIncludesSteepGrades")}
          </div>
        )}

        {/* Cancel + Accessible row */}
        <div className="flex gap-2 px-4 pb-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 flex items-center justify-center rounded-2xl py-3 text-sm font-medium"
            style={{ backgroundColor: "rgba(255,255,255,0.16)", color: fg }}
          >
            {t("common:cancel")}
          </button>
          {onToggleAccessible && (
            <div className="flex-1 flex flex-col gap-1">
              <button
                type="button"
                onClick={overlayAccessible ? undefined : onToggleAccessible}
                disabled={overlayAccessible}
                className="w-full flex items-center justify-center gap-2 rounded-2xl py-3 text-sm font-medium"
                style={{
                  backgroundColor: accessibleMode ? "rgba(255,255,255,0.28)" : "rgba(255,255,255,0.16)",
                  color: fg,
                }}
              >
                {/* Pseudo-checkbox — reflects manual state, not combined */}
                <div
                  className="w-4 h-4 rounded border-2 flex items-center justify-center shrink-0"
                  style={{
                    backgroundColor: manualAccessibleMode ? fg : "transparent",
                    borderColor: manualAccessibleMode ? fg : `${fg}99`,
                  }}
                >
                  {manualAccessibleMode && (
                    <FontAwesomeIcon icon={faCheck} className="h-2.5 w-2.5" style={{ color: bg }} />
                  )}
                </div>
                <FontAwesomeIcon icon={faWheelchair} className="h-4 w-4" />
                <span>{t("directions.accessibleRouteShort")}</span>
              </button>
              {overlayAccessible && (
                <p className="text-center text-xs" style={{ opacity: 0.6, color: fg }}>
                  {t("directions.accessibleForcedByFilters")}
                </p>
              )}
            </div>
          )}
        </div>

        {/* "View all steps" collapsible */}
        {remainingInstructions.length > 0 && (
          <Collapsible open={isExpanded} onOpenChange={setIsExpanded} className="flex flex-col min-h-0">
            <CollapsibleTrigger asChild>
              <button
                className="w-full flex-shrink-0 px-4 py-3 flex items-center justify-between text-sm font-medium border-t"
                style={{ borderColor: "rgba(255,255,255,0.16)", color: fg }}
              >
                <span>{t("directions.viewAllSteps")}</span>
                {isExpanded ? (
                  <FontAwesomeIcon icon={faChevronUp} className="h-4 w-4" style={{ opacity: 0.7 }} />
                ) : (
                  <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4" style={{ opacity: 0.7 }} />
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="min-h-0 flex-1 overflow-hidden">
              <div className="max-h-[30vh] overflow-y-auto">
                {remainingInstructions.map((instruction, index) => (
                  <div
                    key={`${index}-${instruction.directionType}`}
                    className="flex items-start gap-3 px-4 py-3 border-t"
                    style={{ borderColor: "rgba(255,255,255,0.10)" }}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                      style={{ backgroundColor: "rgba(255,255,255,0.10)" }}
                    >
                      {(() => {
                        const entry = DIRECTION_ICON_MAP[instruction.directionType];
                        return entry ? (
                          <FontAwesomeIcon
                            icon={entry.icon}
                            className={`h-4 w-4 ${entry.rotate ?? ""}`}
                            style={{ color: fg }}
                          />
                        ) : (
                          <FontAwesomeIcon icon={faLocationArrow} className="h-4 w-4" style={{ color: fg }} />
                        );
                      })()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm break-words" style={{ color: fg }}>
                        {getInstructionText(instruction, t)}
                      </p>
                      {instruction.type === "turn" && instruction.distance > 0 && (
                        <span className="text-xs" style={{ opacity: 0.7 }}>
                          {formatDistance(instruction.distance, i18n.language, t)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </VisitorDrawer>
  );
}
